import { ArrowForward } from "@mui/icons-material";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { format, startOfToday } from "date-fns";
import React from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";

const GOLD = "#ffc000";
const PRIMARY = "#000c66";

export const EventsShowcase = () => {
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();

  React.useEffect(() => {
    const fetch = async () => {
      try {
        // Only upcoming events: anything dated today or later.
        const q = query(
          collection(db, "events"),
          where("eventdate", ">=", startOfToday()),
          orderBy("eventdate", "asc")
        );
        const snap = await getDocs(q);
        setEvents(
          snap.docs.map((d) => ({
            _id: d.id,
            ...d.data(),
            eventdate: d.data().eventdate?.toDate() || new Date(),
          }))
        );
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const featured = events.slice(0, 2);

  return (
    <section
      style={{ background: "#fff", position: "relative", overflow: "hidden" }}
    >
      {/* Faint cross watermark */}
      <svg
        style={{
          position: "absolute",
          right: -60,
          bottom: -60,
          opacity: 0.03,
          pointerEvents: "none",
        }}
        width={420}
        height={420}
        viewBox="0 0 200 200"
        fill={PRIMARY}
      >
        <rect x="88" y="10" width="24" height="180" rx="4" />
        <rect x="10" y="68" width="180" height="24" rx="4" />
        <circle
          cx="100"
          cy="100"
          r="94"
          stroke={PRIMARY}
          strokeWidth="1"
          fill="none"
          strokeDasharray="6 6"
        />
      </svg>

      <div
        className="mx-auto px-4 py-16 md:py-24"
        style={{ maxWidth: 860, position: "relative", zIndex: 1 }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px w-8" style={{ background: `${GOLD}88` }} />
              <p
                className="font-abel uppercase tracking-[0.4em] text-xs"
                style={{ color: GOLD }}
              >
                What's Coming
              </p>
              <div className="h-px w-8" style={{ background: `${GOLD}88` }} />
            </div>
            <h2
              className="font-aref font-bold leading-tight"
              style={{
                color: PRIMARY,
                fontSize: "clamp(1.75rem, 3.5vw, 2.8rem)",
              }}
            >
              Upcoming <span style={{ color: GOLD }}>Events</span>
            </h2>
          </div>

          <button
            onClick={() => navigate("/events")}
            className="self-start sm:self-auto flex items-center gap-2 font-abel text-sm uppercase tracking-widest px-6 py-2.5 rounded-full transition-all duration-200 hover:scale-105"
            style={{
              border: `1px solid ${PRIMARY}44`,
              color: PRIMARY,
              background: "transparent",
            }}
          >
            View All
            <ArrowForward style={{ fontSize: 14, color: PRIMARY }} />
          </button>
        </div>

        {/* Cards */}
        {loading ? (
          <div className="flex flex-col gap-5">
            {[1, 2].map((i) => (
              <div
                key={i}
                style={{
                  background: "#f3f3f3",
                  borderRadius: 10,
                  height: 180,
                }}
              />
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ border: `2px dashed ${GOLD}66` }}
            >
              <span style={{ color: `${GOLD}88`, fontSize: 22 }}>✦</span>
            </div>
            <p
              className="font-aref text-lg font-bold"
              style={{ color: PRIMARY, opacity: 0.4 }}
            >
              No upcoming events
            </p>
            <p className="font-abel text-sm" style={{ color: "#aaa" }}>
              Check back soon.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            {featured.map((event, idx) => (
              <div
                key={event._id}
                onClick={() => navigate(`/events/view/${event.slug}`)}
                className="flex flex-col sm:flex-row"
                style={{
                  border: `1px solid #ececec`,
                  borderLeft: `4px solid ${GOLD}`,
                  borderRadius: 10,
                  overflow: "hidden",
                  cursor: "pointer",
                  boxShadow: "0 2px 18px rgba(0,0,0,0.06)",
                  transition: "transform 0.22s ease, box-shadow 0.22s ease",
                  minHeight: 180,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    "0 14px 36px rgba(0,12,102,0.10)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 18px rgba(0,0,0,0.06)";
                }}
              >
                {/* Image */}
                <div
                  className="shrink-0 w-full sm:w-56 md:w-64 lg:w-72"
                  style={{ position: "relative", overflow: "hidden", minHeight: 180 }}
                >
                  {event.imgSrc ? (
                    <img
                      src={event.imgSrc}
                      alt={event.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        display: "block",
                        transition: "transform 0.4s ease",
                        position: "absolute",
                        inset: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "#f3f3f3",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ color: `${GOLD}66`, fontSize: 40 }}>✦</span>
                    </div>
                  )}

                  {/* Index badge */}
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      left: 12,
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: GOLD,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span
                      className="font-aref font-bold text-sm"
                      style={{ color: PRIMARY }}
                    >
                      {idx + 1}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div
                  className="flex flex-col justify-center gap-3"
                  style={{ padding: "20px 24px", flex: 1 }}
                >
                  {/* Date + time row */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span
                      className="font-abel font-bold text-xs px-3 py-1 rounded-full"
                      style={{
                        background: `${GOLD}18`,
                        color: PRIMARY,
                        border: `1px solid ${GOLD}44`,
                      }}
                    >
                      {format(new Date(event.eventdate), "do MMM, yyyy")}
                    </span>
                    <span
                      className="font-abel text-xs uppercase tracking-widest"
                      style={{ color: "#bbb" }}
                    >
                      {format(new Date(event.eventdate), "eee · h:mma")}
                    </span>
                  </div>

                  {/* Title */}
                  <h3
                    className="font-aref font-bold line-clamp-2"
                    style={{
                      color: PRIMARY,
                      fontSize: "clamp(1rem, 2vw, 1.25rem)",
                      lineHeight: 1.4,
                    }}
                  >
                    {event.title}
                  </h3>

                  {/* Body excerpt */}
                  <p
                    className="font-abel text-sm line-clamp-2"
                    style={{ color: "#888", lineHeight: 1.65 }}
                    dangerouslySetInnerHTML={{ __html: event.body }}
                  />

                  {/* CTA */}
                  <div className="flex items-center gap-1.5 mt-1">
                    <span
                      className="font-abel text-xs uppercase tracking-widest font-bold"
                      style={{ color: GOLD }}
                    >
                      View Event
                    </span>
                    <ArrowForward style={{ fontSize: 12, color: GOLD }} />
                  </div>
                </div>

                {/* Decorative cross panel */}
                <div
                  className="hidden sm:flex items-center justify-center shrink-0"
                  style={{
                    width: 76,
                    borderLeft: `1px solid ${GOLD}33`,
                    background: `linear-gradient(160deg, ${GOLD}08 0%, #fff 100%)`,
                  }}
                >
                  <svg
                    viewBox="0 0 80 80"
                    width={52}
                    height={52}
                    fill="none"
                  >
                    {/* Outer dashed ring */}
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke={PRIMARY}
                      strokeWidth="1"
                      strokeDasharray="4 4"
                      opacity="0.3"
                    />
                    {/* Inner ring */}
                    <circle
                      cx="40"
                      cy="40"
                      r="28"
                      stroke={GOLD}
                      strokeWidth="0.8"
                      opacity="0.5"
                    />
                    {/* Cross vertical */}
                    <rect
                      x="35"
                      y="16"
                      width="10"
                      height="48"
                      rx="2"
                      fill={GOLD}
                      stroke={PRIMARY}
                      strokeWidth="1.2"
                    />
                    {/* Cross horizontal */}
                    <rect
                      x="16"
                      y="30"
                      width="48"
                      height="10"
                      rx="2"
                      fill={GOLD}
                      stroke={PRIMARY}
                      strokeWidth="1.2"
                    />
                    {/* Center jewel */}
                    <circle cx="40" cy="40" r="4" fill={PRIMARY} />
                    <circle cx="40" cy="40" r="2" fill={GOLD} />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
